import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { FilterChange } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import { useCurrentFilterStatus } from '@/app/components/hotel-detail/CurrentFilterStatusProvider';
import useTransportsFetch, {
  isTravelTransportsResponseError,
} from '@/app/components/hotel-detail/travel-api/transportsFetch';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useTransportPageFilterTracking } from './hooks/useTransportPageFilterTracking';

type TransportChangeTransportFilterProviderProps = {
  children: ReactNode;
};

type ChangeTransportFiltersContextType = {
  changeFilter: (filterChange: FilterChange) => void;
  clearAll: () => void;
};

type ChangeTransportIsChangingContextType = {
  isChanging: boolean;
  changingFilter: TransportFiltersOrigin | undefined;
  setChanging: Dispatch<SetStateAction<boolean>>;
};

const ChangeTransportFiltersContext = createContext<ChangeTransportFiltersContextType | undefined>(undefined);
const ChangeTransportIsChangingContext = createContext<ChangeTransportIsChangingContextType | undefined>(undefined);

export function ChangeTransportFilterProvider(props: TransportChangeTransportFilterProviderProps) {
  const { children } = props;
  const { currentFiltersStatus = undefined, setCurrentFiltersStatus } = useCurrentFilterStatus();
  const {
    data: transportsFetchData,
    isValidating: isValidatingTransports,
    error: errorTransport,
    isFallbackTransport,
  } = useTransportsFetch();

  useEffect(() => {
    if (!errorTransport && !isTravelTransportsResponseError(transportsFetchData) && !isFallbackTransport) {
      setCurrentFiltersStatus((transportsFetchData as FullAvailableTransportResponse).filters);
    }
  }, [errorTransport, setCurrentFiltersStatus, transportsFetchData, isFallbackTransport]);

  const [isChanging, setChanging] = useState(false);
  const [changingFilter, setChangingFilter] = useState<TransportFiltersOrigin | undefined>();
  const { sendCuriosityClickEvent } = useCuriosity();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  useEffect(() => {
    if (isValidatingTransports) {
      setChanging(true);
    } else {
      setChanging(false);
    }
  }, [isValidatingTransports]);

  async function changeFilter(filterChange: FilterChange) {
    const { newState, event } = filterChange;
    setChangingFilter(event.origin);
    const newFilters = { ...currentFiltersStatus, ...newState };

    if (event && event.type === 'FILTER' && event.origin === 'FILTER_RESET') {
      setCurrentFiltersStatus(resetFilters(newFilters));
    } else {
      setCurrentFiltersStatus(newFilters);
    }
  }

  function clearAll() {
    sendStratrackFlightPageFilterEvent({
      filterName: 'FILTER_RESET',
      value: 'true',
      deselectFlag: false,
    });
    sendCuriosityClickEvent({
      event_name: 'click_on_reset_all_filters',
      event_context: 'change_transport_page',
    });
    if (isAnyFilterSelected(currentFiltersStatus)) {
      changeFilter({ event: { type: 'FILTER', origin: 'FILTER_RESET' } });
    }
  }

  const memoizedValue = useMemo(
    () => ({
      clearAll,
      changeFilter,
    }),
    [currentFiltersStatus]
  );

  return (
    <ChangeTransportFiltersContext.Provider value={memoizedValue}>
      <ChangeTransportIsChangingContext.Provider
        value={useMemo(() => ({ isChanging, setChanging, changingFilter }), [isChanging, setChanging, changingFilter])}
      >
        {children}
      </ChangeTransportIsChangingContext.Provider>
    </ChangeTransportFiltersContext.Provider>
  );
}

export function useTransportChangeFilters() {
  const context = useContext(ChangeTransportFiltersContext);

  if (context === undefined) {
    throw new Error('useTransportChangeFilters must be within a ChangeTransportFilterProvider');
  }

  return context;
}

export function useTransportIsChangingFilters() {
  const context = useContext(ChangeTransportIsChangingContext);

  if (context === undefined) {
    throw new Error('useTransportIsChangingFilters must be within a ChangeTransportFilterProvider');
  }

  return context;
}

export function isAnyFilterSelected(filterStatus: TransportFiltersResponse | undefined) {
  if (filterStatus) {
    const sameAirportFilters = [
      'inboundSameAirport',
      'outboundSameAirport',
      'inboundSameStation',
      'outboundSameStation',
    ];
    return (
      Object.entries(filterStatus)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([name, _]) => name === 'directOnly')
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .some(([_, filter]) => (filter as DirectOnlyFilter).selected) ||
      Object.entries(filterStatus)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([name, _]) => sameAirportFilters.includes(name))
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .some(([_, filter]) => (filter as SameAirportFilter).on) ||
      Object.entries(filterStatus)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([name, _]) => ![...sameAirportFilters, 'directOnly', 'sortOptions'].includes(name))
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .some(([_, filter]) => (filter as TransportFilter[]).some((val) => val.selected))
    );
  }
  return false;
}

function resetFilters(response: TransportFiltersResponse): TransportFiltersResponse {
  const resetResponse: TransportFiltersResponse = { ...response };

  // eslint-disable-next-line no-restricted-syntax
  for (const key in resetResponse) {
    if (key !== 'sortOptions') {
      const filter = resetResponse[key as keyof TransportFiltersResponse];
      if (Array.isArray(filter)) {
        (resetResponse[key as keyof TransportFiltersResponse] as unknown[]) = filter.map((item) => ({
          ...item,
          selected: false,
        }));
      } else if (typeof filter === 'object' && filter !== null && key === 'directOnly') {
        (resetResponse[key as keyof TransportFiltersResponse] as DirectOnlyFilter).selected = false;
      } else if (typeof filter === 'object' && filter !== null) {
        (resetResponse[key as keyof TransportFiltersResponse] as SameAirportFilter).on = false;
      }
    }
  }

  return resetResponse;
}
